/// <amd-module name="Repos/RepoDropdown/Components/RepositoryDropdownWithActions" />
import React = require("react");

import { GitImportRequest } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { IProjectRepositoryInformation, IProjectRepositoryInformationService } from "Repos/Common/ProjectRepositoryInformationService";
import { IReposRoutingService } from "Repos/Common/ReposRoutingService";
import * as Resources from "Repos/RepoDropdown/Resources";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssKeyboardShortcutService } from "VSS/Platform/KeyboardShortcuts";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IStartRightExtensionService } from "VSS/Platform/StartRightExtension";
import { IButtonProps } from "VSSUI/Button";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { noop } from "VSSUI/Util";
import { IRepositoryDropdownItem } from "../RepositoryDropdownProvider";
import { RepositoryDropdown } from "./RepositoryDropdown";

interface IRepositoryDropdownWithActionsProps {
    registerKeyboardShortcuts?: boolean;
}

class RepositoryDropdownWithActions extends VssComponent<IRepositoryDropdownWithActionsProps> {
    private vcData?: IProjectRepositoryInformation;
    private projectId?: string;
    private projectName?: string;
    private canCreateRepositories?: boolean;
    private canManageRepositories?: boolean;
    private repositoryDropdown = React.createRef<RepositoryDropdown>();
    private repositoryContributedActions: IReadonlyObservableArray<IMenuItem>;

    constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        const projectInfoService = context.pageContext.getService<IProjectRepositoryInformationService>("IProjectRepositoryInformationService");
        this.vcData = projectInfoService.getData();

        const tfsPageService = context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();

        if (tfsPageData && tfsPageData.project) {
            this.projectName = tfsPageData.project.name;
            this.projectId = tfsPageData.project.id;

            const permissionService = context.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

            this.canCreateRepositories = permissionService.hasPermission(
                GitRepositoryPermissions.CreateRepository,
                undefined,
                undefined,
                this.projectId
            );
            this.canManageRepositories =
                permissionService.hasPermission(GitRepositoryPermissions.RenameRepository, undefined, undefined, this.projectId) ||
                permissionService.hasPermission(GitRepositoryPermissions.DeleteRepository, undefined, undefined, this.projectId) ||
                permissionService.hasPermission(GitRepositoryPermissions.ManagePermissions, undefined, undefined, this.projectId);
        } else {
            this.projectName = undefined;
            this.projectId = undefined;
        }

        this.repositoryContributedActions = getMenuItems(this.context.pageContext, "ms.vss-code-web.command-bar-repository-picker-actions");
    }

    public render(): JSX.Element | null {
        return this.vcData && this.projectName ? (
            <Observer contributedActions={this.repositoryContributedActions}>
                {(props: { contributedActions: IMenuItem[] }) => {
                    return (
                        <RepositoryDropdown
                            ref={this.repositoryDropdown}
                            actions={this.getActions(props.contributedActions)}
                            selectedRepositoryItem={this.getSelectedRepositoryItem()}
                            providerOptions={{
                                getArtifactHref: this.getItemHref,
                                projectName: this.projectName,
                                projectId: this.projectId,
                                supportsTfvc: this.vcData && this.vcData.supportsTfvc
                            }}
                        />
                    );
                }}
            </Observer>
        ) : null;
    }

    public componentDidMount(): void {
        super.componentDidMount();

        if (this.props.registerKeyboardShortcuts) {
            const shortcutsService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
            shortcutsService.registerShortcut("Repos", {
                action: () => {
                    const dropdown = this.repositoryDropdown.current;
                    if (dropdown) {
                        dropdown.focus();
                        dropdown.expand();
                    }
                },
                combos: ["r"],
                description: Resources.SelectRepositoryShortcut
            });
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        const shortcutsService = this.context.pageContext.getService<IVssKeyboardShortcutService>("IVssKeyboardShortcutService");
        shortcutsService.unRegisterShortcut("Repos", "r");
    }

    private getActions(contributedActions: IMenuItem[]): IButtonProps[] {
        return [
            ...this.getDefaultActions(contributedActions),
            ...this.getContributedActions(contributedActions)
        ];
    }

    private getDefaultActions = (contributedActions: IMenuItem[]): IButtonProps[] => {
        const actions: IButtonProps[] = [];

        if (this.canCreateRepositories) {
            // New Repository Action
            actions.push({
                text:Resources.NewRepository,
                iconProps: { iconName: "Add" },
                onClick: this.onCreate,
                className: "repo-dropdown-action"
            });

            // Import Repository Action
            actions.push({
                text: Resources.ImportRepository,
                iconProps: { iconName: "Upload" },
                onClick: this.onImport,
                className: "repo-dropdown-action"
            });
        }

        if (this.canManageRepositories) {
            // Manage Repositories Action
            const adminUrl = routeUrl(this.context.pageContext, "ms.vss-code-web.admin-version-control-route", {
                project: this.projectName!
            });
            actions.push({
                text: Resources.ManageRepositories,
                iconProps: { iconName: "Settings" },
                href: adminUrl,
                className: "repo-dropdown-action"
            });
        }

        return actions;
    };

    private getContributedActions = (contributedActions: IMenuItem[]): IButtonProps[] => {
        return this.menuItemsToActions(contributedActions);
    }

    private getItemHref = (repositoryItem: IRepositoryDropdownItem) => {
        const routingService = this.context.pageContext.getService<IReposRoutingService>("IReposRoutingService");
        return routingService.getSwitchRepositoryURL(repositoryItem.isTfvc ? null : repositoryItem.name, this.projectName);
    };

    private getSelectedRepositoryItem = (): IRepositoryDropdownItem | undefined => {
        if (this.vcData) {
            return {
                id: this.vcData.defaultRepoId || "tfvc",
                name: this.vcData.defaultRepoIsGit ? this.vcData.defaultGitRepoName : "$/" + this.projectName,
                isTfvc: !this.vcData.defaultRepoIsGit,
                isFork: this.vcData.defaultRepoIsFork
            };
        }
        return undefined;
    };

    private menuItemToAction = (item: IMenuItem): IButtonProps => {
        return {
            text: item.text || "",
            iconProps: item.iconProps,
            onClick: event => {
                if (item.onActivate) {
                    item.onActivate(item, event);
                }
            },
            href: item.href,
            className: "repo-dropdown-action"
        }
    };

    private menuItemsToActions = (menuItems: IMenuItem[]): IButtonProps[] => {
        const actions: IButtonProps[] = [];

        menuItems.forEach(item => {
            actions.push(this.menuItemToAction(item));
        });

        return actions;
    };

    private onCreate = () => {
        const startRightExtensionService = this.context.pageContext.getService<IStartRightExtensionService>("IStartRightExtensionService");
        if (startRightExtensionService.navigateToCreateRepository(this.context.pageContext)) {
            return;
        }

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const onCreate = (createOperation: Promise<{ repository: { webUrl: string } }>) => {
                createOperation.then(result => {
                    FastPageSwitch(this.context.pageContext, result.repository.webUrl, true);
                }, noop);
            };

            return (
                <WrappedComponent
                    dependencies={["ms.vss-code-web.import-dependencies"]}
                    wrappedProps={{
                        onCreate,
                        onDismiss,
                        projectId: this.projectId,
                        projectName: this.projectName
                    }}
                    wrappedType="ms.vss-code-web.panel-create"
                />
            );
        });
    };

    private onImport = () => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const onImport = (importOperation: Promise<GitImportRequest>) => {
                importOperation.then(result => {
                    FastPageSwitch(this.context.pageContext, result.repository.webUrl, true);
                }, noop);
            };

            return (
                <WrappedComponent
                    dependencies={["ms.vss-code-web.import-dependencies"]}
                    wrappedProps={{ onDismiss, onImport, projectId: this.projectId, projectName: this.projectName }}
                    wrappedType="ms.vss-code-web.panel-import"
                />
            );
        });
    };
}

Components.add("ms.vss-code-web.repository-dropdown-actions", RepositoryDropdownWithActions);

function RespositoryDropdownMenuItem(): JSX.Element {
    return (
        <tr>
            <td />
            <td colSpan={5}>
                <div className="repository-dropdown-menuitem flex-row">
                    <RepositoryDropdownWithActions />
                </div>
            </td>
            <td />
        </tr>
    );
}

Components.add("ms.vss-code-web.repository-dropdown-menuitem", RespositoryDropdownMenuItem);
